/**
 * BenaPC's own price ranges — one source for the services page and the price
 * estimator (2026-10-10). Labour; parts by model where noted. Bringing a
 * computer in for a lab check costs PRICES.labCheck (owner 2026-10-14); the
 * repair price is given in writing after the check, before any work. The
 * consultation (WhatsApp, phone, photos, /diagnose) is free. Never say whether
 * the check fee is credited toward a repair — that isn't decided.
 *
 * Not the same thing as /guides/computer-repair-prices-israel, which quotes
 * typical *market* prices in Israel for comparison.
 */
export interface PriceItem {
  min: number;
  max?: number;          // no max → "from" (unless exact)
  exact?: boolean;       // a fixed price
  plusPart?: boolean;    // + the part's cost
}

export const PRICES = {
  // Market check 2026-10-10 (Midrag customer-paid averages 2025, homecomp,
  // d.co.il) — the owner flagged ₪100 for a Windows install as far too low.
  labCheck: { min: 150, exact: true },         // bringing a computer in for a check (owner 2026-10-14)
  generalRepair: { min: 200 },                 // diagnostic visit ₪211-₪320
  laptopScreen: { min: 500, max: 1400 },       // non-touch ~₪600-700, touch avg ₪1,330
  keyboard: { min: 450, max: 900 },            // avg ₪711 (₪495-₪1,045)
  battery: { min: 350, max: 750 },             // ~₪670; MacBook ~₪965
  cleaning: { min: 250, max: 450 },            // cleaning + thermal paste, avg ₪392
  ramUpgrade: { min: 150, plusPart: true },    // component install ₪180 + part
  ssdInstall: { min: 250, plusPart: true },    // incl. moving the system + files
  cpuSwap: { min: 250, plusPart: true },       // ₪200-₪300 + CPU
  psuUpgrade: { min: 150, plusPart: true },
  osInstall: { min: 250, max: 350 },           // avg ₪352; with software ~₪550
  virusRemoval: { min: 230, max: 350 },        // ₪230-₪365
  dataRecovery: { min: 300, max: 900 },        // logical; physical damage → quote
  optimization: { min: 200, exact: true },     // software fault fix avg ₪240
  wifiSetup: { min: 200, max: 400 },
  printerSetup: { min: 180, exact: true },     // ₪180
  firstSetup: { min: 250, exact: true },
  appInstall: { min: 100, exact: true },
} satisfies Record<string, PriceItem>;

/** Remote support per hour — owner's decision 2026-10-10 (was ₪250/hour on
 *  the services page and "from ₪80" on the area pages). */
export const REMOTE_SUPPORT_HOURLY = 150;

export const shekel = (n: number) => `₪${n.toLocaleString('he-IL')}`;

/** "₪350-₪900", "החל מ-₪150", "₪100 + עלות רכיב" — as the services page shows them. */
export function priceLabel(p: PriceItem, part = 'עלות רכיב'): string {
  if (p.plusPart) return `${shekel(p.min)} + ${part}`;
  if (p.max) return `${shekel(p.min)}-${shekel(p.max)}`;
  if (p.exact) return shekel(p.min);
  return `החל מ-${shekel(p.min)}`;
}
